// ProcessImageForUpload.js
// @author: anghello

const ProcessImageForUpload = async (
  file,
  exifJsSrc,
  minUploadPhotoWidth,
  minUploadPhotoHeight,
  maxUploadPhotoWidth,
  maxUploadPhotoHeight,
  disableOrientation = false,
  quality = 0.9
) => {
  if (!file) {
    throw new Error('file is required');
  }

  if (file.type && file.type.startsWith('video/')) {
    return file;
  }

  if (file.type && !file.type.startsWith('image/')) {
    throw new Error('File must be an image');
  }

  if (!minUploadPhotoWidth) {
    minUploadPhotoWidth = 1280;
  }

  if (!minUploadPhotoHeight) {
    minUploadPhotoHeight = 720;
  }

  if (!maxUploadPhotoWidth) {
    maxUploadPhotoWidth = 2731;
  }

  if (!maxUploadPhotoHeight) {
    maxUploadPhotoHeight = 1536;
  }

  if (!exifJsSrc && !disableOrientation) {
    throw new Error('exifJsSrc is required');
  }

  /* Internal functions: START */
  async function loadExifJs() {
    if (window.EXIF) {
      return Promise.resolve();
    } else {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = exifJsSrc;
        script.onload = () => {
          if (!window.EXIF) {
            reject(new Error('EXIF library failed to load'));
          } else {
            resolve();
          }
        };
        script.onerror = (err) => reject(err);
        document.head.appendChild(script);
      });
    }
  }

  function readFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => resolve(reader.result);
      reader.onerror = err => reject(err);

      reader.readAsDataURL(file);
    });
  }

  function blobToFile(blob, fileName, mimeType) {
    return new File([blob], fileName, { type: mimeType });
  }

  function isCanvasSizeSupported(width, height) {
    const maxCanvasSize = 32767;
    return width <= maxCanvasSize && height <= maxCanvasSize;
  }

  function requiresRotation(orientation) {
    return [5, 6, 7, 8].includes(orientation);
  }
  /* Internal functions: END */

  let orientation = 1;

  if (!disableOrientation) {
    await loadExifJs();

    /* eslint-disable */
    orientation = await new Promise((resolve) => {
      EXIF.getData(file, function () {
        resolve(EXIF.getTag(this, 'Orientation') || 1);
      });
    });
    /* eslint-enable */
  }

  const dataURL = await readFile(file);

  const img = await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = dataURL;
  });

  let { width, height } = img;
  let newWidth = width;
  let newHeight = height;

  if (requiresRotation(orientation)) {
    [newWidth, newHeight] = [newHeight, newWidth];
  }

  const widthMinRatio = minUploadPhotoWidth / newWidth;
  const heightMinRatio = minUploadPhotoHeight / newHeight;
  const minScale = Math.max(widthMinRatio, heightMinRatio, 1);

  const widthMaxRatio = maxUploadPhotoWidth / newWidth;
  const heightMaxRatio = maxUploadPhotoHeight / newHeight;
  const maxScale = Math.min(widthMaxRatio, heightMaxRatio, 1);

  let scale = 1;
  if (minScale > 1) {
    scale = minScale;
  } else if (maxScale < 1) {
    scale = maxScale;
  }

  newWidth = Math.round(newWidth * scale);
  newHeight = Math.round(newHeight * scale);

  if (newWidth === width && newHeight === height && orientation === 1) {
    return file;
  }

  const canvas = document.createElement('canvas');

  if (!disableOrientation && requiresRotation(orientation)) {
    canvas.width = newHeight;
    canvas.height = newWidth;
  } else {
    canvas.width = newWidth;
    canvas.height = newHeight;
  }

  if (!isCanvasSizeSupported(canvas.width, canvas.height)) {
    throw new Error('Canvas size not supported on this device');
  }

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get 2D context from canvas');
  }

  if (!disableOrientation) {
    switch (orientation) {
      case 2: // horizontal flip
        ctx.transform(-1, 0, 0, 1, newWidth, 0);
        break;
      case 3: // 180° rotation
        ctx.transform(-1, 0, 0, -1, newWidth, newHeight);
        break;
      case 4: // vertical flip
        ctx.transform(1, 0, 0, -1, 0, newHeight);
        break;
      case 5: // horizontal flip + 90° CCW
        ctx.transform(0, 1, 1, 0, 0, 0);
        break;
      case 6: // 90° CW
        ctx.transform(0, 1, -1, 0, newHeight, 0);
        break;
      case 7: // horizontal flip + 90° CW
        ctx.transform(0, -1, -1, 0, newHeight, newWidth);
        break;
      case 8: // 90° CCW
        ctx.transform(0, -1, 1, 0, 0, newWidth);
        break;
      default: // case 1: no rotation
        break;
    }
  }

  ctx.drawImage(img, 0, 0, newWidth, newHeight);

  const mimeType = file.type.startsWith("image/") ? file.type : "image/jpeg";
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((b) => {
      if (b) resolve(b);
      else reject(new Error('Failed to create blob from canvas'));
    }, mimeType, quality);
  });
  
  const newFile = blobToFile(blob, file.name, mimeType);
  return newFile;
}

export default ProcessImageForUpload;